import React, { useEffect } from 'react';
import { Modal, View } from 'react-native';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import { WindowInformation } from '../../../services/window-information';
import { dispatchService } from '../../../services/central-api/dispatch';
import { ClientError } from '../../../services/central-api/base-service';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import { useForm } from 'react-hook-form';

interface ModalProps {
    eventOperation?: EventOperation;
    isModalVisible: boolean;
    authenticatedUser?: AuthenticatedUser | null;
    onClose: () => void;
}

interface EventOperationForm {
    name: string;
    actingBodyId: number;
}

export default function EventOperationModal({ onClose, eventOperation, isModalVisible, authenticatedUser }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const defaultEmptyValues: EventOperationForm = {
        name: '',
        actingBodyId: 0
    };

    const form = useForm<EventOperationForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    function clearValues() {
        form.reset(defaultEmptyValues);
    }

    async function loadPageInfo() {
        if (!authenticatedUser) {
            return;
        }
        if (eventOperation) {
            form.setValue('name', eventOperation.name);
            form.setValue('actingBodyId', eventOperation.actingBody.id);
        } else if (!(authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_event_operations) && authenticatedUser.actingBodyUnit.actingBodyId) {
            form.setValue('actingBodyId', authenticatedUser.actingBodyUnit.actingBodyId);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    async function formSubmit() {
        try {
            const values = form.getValues();
            if (eventOperation?.id) {
                await dispatchService.updateEventOperation({
                    id: eventOperation.id,
                    name: values.name,
                });
            } else {
                await dispatchService.createEventOperation({
                    name: values.name,
                    actingBodyId: values.actingBodyId
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onClose();
            clearValues();
        } catch (err) {
            if (err instanceof ClientError) {
                Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            } else {
                console.error(err);
                Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
            }
        }
    }

    return (
        <Modal visible={isModalVisible} transparent animationType='fade' onRequestClose={() => {
            onClose();
            clearValues();
        }}>
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                    {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_event_operations) &&
                        <ActingBodiesDropdown
                            form={form}
                            fieldName='actingBodyId'
                            includeAllOption={false}
                            zIndex={2}
                            disabled={!!eventOperation?.id}
                            required
                        />
                    }
                    <FormInput
                        label={translate('name')}
                        placeholder={translate('name')}
                        form={form}
                        fieldName={'name'}
                        required
                    />
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => {
                            onClose();
                            clearValues();
                        }}
                        disabled={!form.formState.isValid}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet(theme => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    }
}));
